import React, { Fragment, MouseEvent, useContext, useEffect, useState } from 'react'
import { Box, Button, Divider, Grid, Menu, MenuItem, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useParams } from 'react-router-dom'

// others
import { Account, useFetchAccountByIdLazyQuery } from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { AppContext } from '../../../../context'
import { ActionType } from '../../../../context/AppContextReducer'

// component import
import { CardBox, CustomTabButton } from '../../../../theme/styleComponents'
import { ContentHead } from '../../../common/ContentHead'
import { anchorOrigin, menuPaperStyles, transformOrigin } from '../../../../theme/styleConstants'
import { Alert } from '../../../common/Alert'
import { AddNewAccount } from '../../AddNewAccount'
import { ArchiveAccount } from '../ArchiveAccount'
import { formatCurrency } from '../../../../utils'
import TransactionsTable from './TransactionsTable'
import TransferFunds from '../../TransferFunds'

const ChartsOfAccountDetails = () => {
  const { id: accountId } = useParams()
  const [tab, setTab] = React.useState(0)
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [refetchPayments, setRefetchPayments] = useState(new Date().getTime())
  const open = Boolean(anchorEl)
  const { isOpen, isEdit, dispatch } = useContext(AppContext)

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleEditAccount = () => {
    handleMenuClose()
    return dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
  }

  const actionButton = () => (
    <>
      {currentAccount && currentAccount.isBankAccount && (
        <TransferFunds
          sourceAccount={{ label: currentAccount.name, value: currentAccount.id, data: currentAccount }}
          refetch={() => {
            refetch()
            setRefetchPayments(new Date().getTime())
          }}
        />
      )}

      <Button variant="contained" color="primary" endIcon={<ArrowDropDownIcon />} onClick={handleMenuClick}>
        Actions
      </Button>
    </>
  )

  const [fetchAccount, { loading, refetch }] = useFetchAccountByIdLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      Alert.error('Unable to fetch case')
    },

    onCompleted(data) {
      if (data) {
        const { fetchAccountById } = data
        const { account, response } = fetchAccountById || {}
        const { status } = response || {}

        if (status === 200 && account) {
          setCurrentAccount(account as Account)
        }
      }
    }
  })

  const handleArchiveAccount = () => {
    handleMenuClose()
    dispatch({ type: ActionType.SET_IS_EDIT, isEdit: !isEdit })
  }

  useEffect(() => {
    accountId &&
      fetchAccount({
        variables: {
          id: accountId
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId])

  return (
    <CardBox marginTop={3}>
      <ContentHead title={currentAccount?.name || '--'} customButton={actionButton} />

      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            PaperProps={menuPaperStyles}
          >
            <MenuItem onClick={handleEditAccount}>Edit</MenuItem>
            <MenuItem onClick={handleArchiveAccount}>{currentAccount?.active ? 'Archive' : 'Unarchive'}</MenuItem>
          </Menu>
        </Box>

        <Box padding="0px 0px 28px 0px">
          <CustomTabButton
            isLeftItem
            className={tab === 0 ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
            onClick={() => setTab(0)}
            color="primary"
          >
            Details
          </CustomTabButton>
          <CustomTabButton
            className={tab === 1 ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
            onClick={() => setTab(1)}
          >
            Transaction
          </CustomTabButton>
        </Box>

        {tab === 0 && (
          <Grid container spacing={2}>
            {[
              ['Balance', formatCurrency(currentAccount?.currentBalance || 0)],
              ['Account Name', currentAccount?.name || '--'],
              ['Account Type', currentAccount?.category || '--'],
              ['Institutions', currentAccount?.institution || '--'],
              ['Account Number', currentAccount?.accountNo || '--'],
              ['Routing Number', currentAccount?.routingNo || '--'],
              ['Sort Code', currentAccount?.sortCode || '--'],
              ['Opening Balance', formatCurrency(currentAccount?.openingBalance || 0)],
              ['Description', currentAccount?.description]
            ].map(([label, value], index) => (
              <React.Fragment key={index}>
                <Divider style={{ margin: '10px 10px', width: '100%' }} />
                <Grid item xs={6} sm={4}>
                  <Typography fontWeight={700} fontSize={18} letterSpacing={1}>
                    {label}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={8}>
                  <Typography fontWeight={500} fontSize={18} color="secondary.main">
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        )}

        {tab === 1 && accountId && <TransactionsTable accountId={accountId} triggerRefetch={refetchPayments} />}
      </Box>

      {!loading && (
        <Fragment>
          {currentAccount && isOpen && <AddNewAccount title="Edit" defaultValues={currentAccount} refetch={refetch} />}
          <ArchiveAccount
            currentChartOfAccount={currentAccount}
            type={currentAccount?.active ? 'Archive' : 'Unarchive'}
          />
        </Fragment>
      )}
    </CardBox>
  )
}

export default ChartsOfAccountDetails
